<script setup lang="ts">
    import { ref } from 'vue'
    import axios from 'axios'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        accept?: string | null
        ttl?: number | null
        skipConfirmation?: boolean
        hideProgress?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'selected', file: File): void
        (e: 'unselected'): void
        (e: 'progress', percent: number): void
        (e: 'uploaded', payload: any): void
        (e: 'failed'): void
    }>()

    // ---------------- refs ----------------

    const fileInput = ref<HTMLInputElement | null>(null)

    // ---------------- local state ----------------

    const file = ref<File | null>(null)
    const uploadPercentage = ref(0)
    const uploading = ref(false)

    // ---------------- actions ----------------

    function handleFileSelected()
    {
        const input = fileInput.value
        if (!input?.files?.length) return

        file.value = input.files[0]
        emit('selected', file.value)

        if (props.skipConfirmation)
        {
            submitFile()
        }
    }

    function clickFileInput()
    {
        fileInput.value?.click()
    }

    function formatFileSize(size: number)
    {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        if (size === 0) return '0 Byte'

        const i = Math.floor(Math.log(size) / Math.log(1024))
        return Math.round(size / Math.pow(1024, i)) + ' ' + sizes[i]
    }

    function submitFile()
    {
        if (!file.value) return

        const formData = new FormData()
        formData.append('file', file.value)

        uploading.value = true

        axios.post('/blobs/v2/upload/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                ttl: props.ttl
            },
            onUploadProgress: (event) =>
            {
                if (!event.total) return

                uploadPercentage.value = Math.round((event.loaded / event.total) * 100)
                emit('progress', uploadPercentage.value)
            }
        })
            .then(response =>
            {
                emit('uploaded', response.data)
                reset()
            })
            .catch(() =>
            {
                uploading.value = false
                emit('failed')
            })
    }

    function reset()
    {
        file.value = null
        uploadPercentage.value = 0
        uploading.value = false

        if (fileInput.value)
        {
            fileInput.value.value = ''
        }
    }

    function cancelUpload()
    {
        emit('unselected')
        reset()
    }
</script>

<template>
    <div>
        <div v-if="!file">
            <input type="file"
                   ref="fileInput"
                   class="custom-file-input"
                   @change="handleFileSelected"
                   :accept="accept"
                   style="display: none;">
            <div @click="clickFileInput">
                <slot>
                    <button class="btn btn-outline-secondary">Upload file</button>
                </slot>
            </div>
        </div>

        <div v-if="!skipConfirmation && file && !uploading">
            <slot name="file">
                <p>
                    Selected file:
                    <strong>{{ file.name }}</strong>
                    ({{ formatFileSize(file.size) }}).
                    Please confirm to proceed or cancel to select a different file.
                </p>
            </slot>
            <button class="btn btn-primary mr-1" @click="submitFile">Confirm</button>
            <button class="btn btn-secondary" @click="cancelUpload">Cancel</button>
        </div>

        <div v-if="!hideProgress && file && uploading">
            <p>Uploading: {{ uploadPercentage }}%</p>
        </div>
    </div>
</template>
